import { isEmpty } from 'lodash'
import moment from 'moment'
import { createSelector, OutputSelector } from 'reselect'
import { IShipment, ISelectShipmentDetails, IFormattedShipperTrackingSegment } from 'types/shipment'
import {
  TENDERED_SHIPMENT_STATUS,
  PENDING_SHIPMENT_STATUSES,
  ON_HOLD_STATUS,
} from 'apps/booking/constants/shipments'
import { IStore } from 'apps/booking/utils/reducers'
import { formatTrackingSegment } from 'apps/booking/utils/shipmentHelpers'
import { formatStops } from 'utils/formatShipmentStops'
import { FORMATS } from 'constants/datetime'

export const selectShipment = (state: IStore): IShipment | undefined =>
  // Shipment was originally typed incorrectly and/or set to the wrong default
  !isEmpty(state.sidebar.data?.shipment) ? state.sidebar.data?.shipment : undefined

export const isUpdatingShipment = (state: IStore): boolean => state.sidebar.isUpdatingShipment

interface SelectShipmentDetailsSelector
  extends OutputSelector<
    IStore,
    Partial<ISelectShipmentDetails>,
    (res: IShipment) => Partial<ISelectShipmentDetails>
  > {}

export const selectShipmentDetails: SelectShipmentDetailsSelector = createSelector(
  selectShipment,
  shipment => ({
    ...shipment,
    id: shipment?.id,
    stops: shipment && formatStops(shipment),
    commodity: shipment?.stops[0].info.commodity,
    equipmentDetails: shipment?.equipment?.equipmentDetails,
    equipmentName: shipment?.equipment?.name,
    ceilingRate: shipment?.formattedCurrency?.ceilingRateAmount,
    ceilingRatePerMile: shipment?.formattedCurrency?.ceilingRatePerMile,
    listRate: shipment?.formattedCurrency?.listRateAmount,
    listRatePerMile: shipment?.formattedCurrency?.listRatePerMile,
    shipperRate: shipment?.customerCharges?.shipperRate,
    targetRate: shipment?.carrierPayment?.targetCarrierRate,
    shipperTrackingSegment:
      shipment &&
      (formatTrackingSegment(shipment?.shipperTrackingSegment) as IFormattedShipperTrackingSegment),
    ...shipment?.info,
  })
)

export const selectHasInvalidP44TruckNumber = createSelector(
  selectShipmentDetails,
  shipmentDetails => shipmentDetails?.project44Metadata?.needsValidTruckNumber
)

export const selectHasInvalidKtTruckNumber = createSelector(
  selectShipmentDetails,
  shipmentDetails => shipmentDetails?.keepTruckinMetadata?.needsValidTruckNumber
)

const selectShipmentCurrentStopIndex = createSelector(
  selectShipment,
  shipment => shipment?.status.currentStopIndex || 0
)

const selectShipmentStops = createSelector(selectShipment, shipment => shipment?.stops)

export const selectCurrentStopAppointment = createSelector(
  [selectShipmentCurrentStopIndex, selectShipmentStops],
  (index, stops) =>
    stops
      ? stops[index].appointment
      : {
          timezone: FORMATS.defaultTimeZone,
          startAt: moment().format(FORMATS.etaFormat),
          endAt: moment().format(FORMATS.etaFormat),
          appointmentType: '',
        }
)

export const isTenderedShipment = createSelector(selectShipment, shipment =>
  [...TENDERED_SHIPMENT_STATUS, ...ON_HOLD_STATUS].includes(shipment?.status?.key || '')
)

export const isPendingCoveredShipment = createSelector(selectShipment, shipment =>
  PENDING_SHIPMENT_STATUSES.includes(shipment?.status?.key || '')
)

export const selectShipmentAssignedDriver = createSelector(
  selectShipment,
  shipment => shipment?.coveredBid?.driver?.id
)

export const selectShipmentCoveredBid = createSelector(
  selectShipment,
  shipment => shipment?.coveredBid
)
